import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';

import { cn } from '@/lib/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;

const SIDE_ANIMATION = {
  right: {
    position: 'inset-y-0 right-0 h-full w-full max-w-md',
    open: 'data-[state=open]:animate-[slide-in-right_0.3s_var(--ease-out-soft)]',
    closed: 'data-[state=closed]:animate-[slide-out-right_0.2s_ease-in]',
  },
  left: {
    position: 'inset-y-0 left-0 h-full w-[88vw] max-w-sm',
    open: 'data-[state=open]:animate-[slide-in-left_0.3s_var(--ease-out-soft)]',
    closed: 'data-[state=closed]:animate-[slide-out-left_0.2s_ease-in]',
  },
  bottom: {
    position: 'inset-x-0 bottom-0 max-h-[88vh] rounded-t-3xl',
    open: 'data-[state=open]:animate-[slide-in-bottom_0.3s_var(--ease-out-soft)]',
    closed: 'data-[state=closed]:animate-[slide-out-bottom_0.2s_ease-in]',
  },
} as const;

export type SheetSide = keyof typeof SIDE_ANIMATION;

const Overlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function Overlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-[60] bg-brown-950/50 backdrop-blur-[2px]',
        'data-[state=open]:animate-[fade-in_0.25s_ease-out]',
        'data-[state=closed]:animate-[fade-out_0.2s_ease-in]',
        className,
      )}
      {...props}
    />
  );
});

/** Centred modal. */
export const DialogContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideClose?: boolean }
>(function DialogContent({ className, children, hideClose, ...props }, ref) {
  return (
    <DialogPrimitive.Portal>
      <Overlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed top-1/2 left-1/2 z-[70] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
          'max-h-[85vh] overflow-y-auto rounded-3xl border-2 border-sand-300 bg-white p-6 shadow-float',
          'data-[state=open]:animate-[pop-in_0.28s_var(--ease-spring)]',
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && <CloseButton className="top-4 right-4" />}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

/**
 * Edge-anchored panel built on the same primitive, so the cart drawer and the
 * mobile menu inherit Radix's focus trap and scroll lock for free.
 */
export const SheetContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    side?: SheetSide;
    hideClose?: boolean;
  }
>(function SheetContent({ className, children, side = 'right', hideClose, ...props }, ref) {
  const anim = SIDE_ANIMATION[side];

  return (
    <DialogPrimitive.Portal>
      <Overlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-[70] flex flex-col bg-cream-100 shadow-float',
          anim.position,
          anim.open,
          anim.closed,
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && <CloseButton className="top-5 right-5" />}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

function CloseButton({ className }: { className?: string }) {
  return (
    <DialogPrimitive.Close
      className={cn(
        'absolute grid size-9 place-items-center rounded-lg text-brown-700 transition-colors hover:bg-cream-300 hover:text-brown-900',
        className,
      )}
    >
      <X className="size-5" aria-hidden="true" />
      <span className="sr-only">Fechar</span>
    </DialogPrimitive.Close>
  );
}
